import { createStyles, Group, Stack, UnstyledButton } from '@mantine/core';
import type { Automation as CodegenAutomation } from '@repo/api-codegen';
import { trackEvent } from '@repo/common/analytics';
import CollapsableStack from '@repo/common/components/CollapsableStack';
import { EntityType } from '@repo/common/enums/entityType';
import { useNavigate } from '@repo/common/hooks/useNavigate';
import { Text } from '@repo/foundations';
import dayjs from 'dayjs';
import {
	AutomationTriggerType,
	useAuthUser,
	type Automation,
} from '../../../api';
import { StaticProperty } from '../../../components/EntityPageLayout/EntityPropertySidebar';
import { RightSidebarWrapper } from '../../../components/RightSidebar';
import { SecodaEntityIcon } from '../../../components/SecodaEntity/SecodaEntityIcon';

const useStyles = createStyles((theme) => ({
	policyItem: {
		borderRadius: theme.other.borderRadius.sm,
		marginLeft: `-${theme.spacing.xs}`,
		cursor: 'pointer',
		width: '100%',

		'&:hover': {
			backgroundColor: theme.other.getColor('fill/primary/hover'),
		},
	},
}));

export interface AutomationConfigurationSidebarProps {
	automation: Automation | CodegenAutomation;
}

const formatTime = (time: number): string => {
	const durationObject = dayjs.duration(time, 'milliseconds');

	const minutes = durationObject.minutes();
	const hours = durationObject.hours();
	const days = durationObject.days();

	if (days > 0) {
		return `${days} day${days > 1 ? 's' : ''}`;
	}

	if (hours > 0) {
		return `${hours} hour${hours > 1 ? 's' : ''}`;
	}

	return `${minutes} minute${minutes > 1 ? 's' : ''}`;
};

function LinkedPolicyItem({
	policy,
}: {
	policy: { id: string; name: string; icon?: string | null };
}) {
	const { classes } = useStyles();
	const navigate = useNavigate();
	const { user, workspace } = useAuthUser();

	const handleClick = () => {
		trackEvent(
			'linked_policy/sidebar/click',
			{
				policy_id: policy.id,
				automation_context: 'true',
			},
			user,
			workspace
		);
		navigate(`/policy/${policy.id}`);
	};

	return (
		<UnstyledButton className={classes.policyItem} onClick={handleClick}>
			<Group spacing="lg" p="xs" noWrap>
				<SecodaEntityIcon
					entityType={EntityType.policy}
					icon={policy.icon || undefined}
					size={32}
					showOverlay={true}
					isAutomation={true}
				/>
				<Stack spacing={0}>
					<Text size="sm" lineClamp={1} weight="semibold">
						{policy.name}
					</Text>
					<Text size="xs" color="text/secondary/default">
						Policy
					</Text>
				</Stack>
			</Group>
		</UnstyledButton>
	);
}

function AutomationConfigurationSidebar({
	automation,
}: AutomationConfigurationSidebarProps) {
	const lastRun = automation.last_run_at
		? dayjs(automation.last_run_at).fromNow()
		: '--';

	const diff = dayjs(automation.next_run_at).diff();

	const nextRun =
		automation.next_run_at && dayjs(automation.next_run_at).isAfter(dayjs())
			? formatTime(diff)
			: '--';

	const hasLinkedPolicy = automation.linked_policy;

	return (
		<RightSidebarWrapper>
			<Stack>
				<CollapsableStack groupName="Overview">
					<Stack spacing="xs">
						{automation.trigger_type === AutomationTriggerType.SCHEDULE && (
							<StaticProperty type="text" label="Last run" value={lastRun} />
						)}
						{automation.trigger_type === AutomationTriggerType.SCHEDULE && (
							<StaticProperty type="text" label="Next run" value={nextRun} />
						)}
					</Stack>
				</CollapsableStack>

				{hasLinkedPolicy && automation.linked_policy && (
					<CollapsableStack groupName="Linked policy">
						<LinkedPolicyItem policy={automation.linked_policy} />
					</CollapsableStack>
				)}
			</Stack>
		</RightSidebarWrapper>
	);
}

export default AutomationConfigurationSidebar;
